import * as React from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DocumentTitle, PageHeader, Link } from "components/common";
import { Col, Row, MenuItem } from "components/bootstrap";
import { MoreActions } from "components/common/EntityDataTable";
import PaginatedEntityTable from "components/common/PaginatedEntityTable";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { LICENSE_SUBJECTS } from "license/constants";
import LicenseCheck from "license/LicenseCheck";
import usePermissions from "hooks/usePermissions";
import { fetchWatchlists } from "watchlist/hooks/useWatchlistsV2";
import SetDefaultTtlModal from "watchlist/components/SetDefaultTtlModal";
import WatchlistRoutes from "watchlist/Routes";
const WATCHLIST_TABLE_ID = "watchlists";
const EXTERNAL_SEARCH = { query: "" };
const COLUMNS = ["name", "description", "entry_count", "default_ttl_seconds"];
const WATCHLIST_ATTRIBUTES = [
  { id: "name", title: "Name", sortable: false },
  { id: "description", title: "Description", sortable: false },
  { id: "entry_count", title: "Entries", sortable: false },
  { id: "default_ttl_seconds", title: "Default TTL", sortable: false }
];
const DEFAULT_LAYOUT = {
  entityTableId: WATCHLIST_TABLE_ID,
  defaultPageSize: 20,
  defaultSort: { attributeId: "name", direction: "asc" },
  defaultDisplayedAttributes: ["name", "entry_count", "default_ttl_seconds"],
  defaultColumnOrder: COLUMNS
};
const TTL_UNITS = ["DAYS", "HOURS", "MINUTES", "SECONDS"];
const formatTtl = (seconds) => {
  if (!seconds || seconds <= 0) {
    return "No expiry";
  }
  const { duration, unit } = extractDurationAndUnit(seconds * 1e3, TTL_UNITS);
  const label = unit.toLowerCase();
  return `${duration} ${duration === 1 ? label.replace(/s$/, "") : label}`;
};
const columnRenderers = {
  attributes: {
    name: {
      renderCell: (_name, watchlist) => /* @__PURE__ */ React.createElement(Link, { to: WatchlistRoutes.DETAILS(watchlist.id) }, watchlist.name)
    },
    entry_count: {
      renderCell: (_entryCount, watchlist) => /* @__PURE__ */ React.createElement("span", null, watchlist.entry_count),
      staticWidth: 80
    },
    default_ttl_seconds: {
      renderCell: (_ttl, watchlist) => /* @__PURE__ */ React.createElement("span", null, formatTtl(watchlist.default_ttl_seconds)),
      staticWidth: 100
    }
  }
};
const watchlistsKeyFn = (searchParams) => ["watchlists", "v2", "overview", searchParams];
const fetchWatchlistsPaginated = (searchParams) => fetchWatchlists().then((all) => {
  var _a;
  const query = ((_a = searchParams.query) != null ? _a : "").trim().toLowerCase();
  const filtered = query ? all.filter(
    (watchlist) => [watchlist.name, watchlist.description, watchlist.type].some(
      (field) => (field != null ? field : "").toLowerCase().includes(query)
    )
  ) : all;
  const start = (searchParams.page - 1) * searchParams.pageSize;
  const list = filtered.slice(start, start + searchParams.pageSize);
  return {
    list,
    pagination: { total: filtered.length },
    attributes: WATCHLIST_ATTRIBUTES,
    meta: null
  };
});
const WatchlistsOverviewPage = () => {
  const { isPermitted } = usePermissions();
  const canEdit = isPermitted("watchlist:edit");
  const queryClient = useQueryClient();
  const [ttlTarget, setTtlTarget] = useState(null);
  const renderEntityActions = (watchlist) => /* @__PURE__ */ React.createElement(MoreActions, null, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: () => setTtlTarget(watchlist) }, "Set default TTL"));
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: "Watchlists" }, /* @__PURE__ */ React.createElement(PageHeader, { title: "Watchlists" }, /* @__PURE__ */ React.createElement("span", null, "Manage the entries of watchlists. Message field values matching a watchlist entry are flagged in the message details. Their values can also be used as a query parameter in searches, dashboards, or event definitions to only include messages with fields that have values in the selected watchlist.")), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(LicenseCheck, { licenseSubject: LICENSE_SUBJECTS.security, featureName: "watchlists", hideChildren: true }, /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: "watchlists",
      tableLayout: DEFAULT_LAYOUT,
      fetchEntities: fetchWatchlistsPaginated,
      keyFn: watchlistsKeyFn,
      entityAttributesAreCamelCase: false,
      columnRenderers,
      entityActions: canEdit ? renderEntityActions : void 0,
      externalSearch: EXTERNAL_SEARCH
    }
  )))), ttlTarget && /* @__PURE__ */ React.createElement(
    SetDefaultTtlModal,
    {
      watchlistId: ttlTarget.id,
      watchlistName: ttlTarget.name,
      currentTtlSeconds: ttlTarget.default_ttl_seconds,
      onClose: () => setTtlTarget(null),
      onUpdated: () => queryClient.invalidateQueries({ queryKey: ["watchlists", "v2"] })
    }
  ));
};
export default WatchlistsOverviewPage;
